// Who sees Falcon CRM in the menu, and who sees only Falcon CRM (the server decides what each person may do: lib/crm.ts
// and the CRM-only check in app.ts). Kept apart from the CRM screens so the shell, the start page and Settings can ask.
import { state } from "../state.js";

/** Falcon CRM is open to this person: the organisation subscribes and they have Falcon CRM ticked (Falcon support: always). */
export function crmOpen(): boolean {
  const s = state.session;
  if (s.mode !== "organisation" || !s.organisation?.crmEnabled) return false;
  return !!s.actor?.supportAccess || !!s.membership?.crmAccess;
}
/** Falcon CRM and no Falcon PLM: only the CRM menu (plus Settings for Admins and Help), never a PLM screen. */
export function crmOnly(): boolean {
  const s = state.session;
  return crmOpen() && !s.actor?.supportAccess && !!s.membership && !s.membership.plmAccess;
}
/** Addresses a CRM-only person may open (anything else goes to Today). */
export function crmOnlyPath(path: string): boolean {
  return /^\/app\/(crm(\/.*)?|help(\/.*)?|account)$/.test(path)
    || /^\/app\/(users|settings|settings\/(profile|crm|digest|email-log|activity|backup))$/.test(path);
}

// ---- Two portals: Falcon PLM and Falcon CRM ---------------------------------------------------------------------------
// Someone with both switches with the name at the top of the menu. CRM screens are in the CRM portal, PLM screens in the
// PLM portal; Help, My account, Users and the shared Settings pages stay in whichever portal the person was last in.
// Same sign-in and address; the last portal used opens after sign-in.
const CRM_ADDR = /^\/app\/(crm(\/.*)?|settings\/crm)$/;
const SHARED_ADDR = /^\/app\/(help(\/.*)?|account|users|settings(\/(profile|digest|email-log|activity|backup))?)$/;
const portalKey = () => `falcon_portal:${state.session.organisation?.id || ""}`;

/** Falcon PLM and Falcon CRM both: the portal switcher shows. */
export function bothPortals(): boolean {
  return crmOpen() && !crmOnly();
}
/** The portal the person last used ("plm" until they open Falcon CRM). */
export function lastPortal(): "plm" | "crm" {
  try { return localStorage.getItem(portalKey()) === "crm" ? "crm" : "plm"; } catch { return "plm"; }
}
export function rememberPortal(p: "plm" | "crm"): void {
  try { localStorage.setItem(portalKey(), p); } catch { /* private window */ }
}
/** This address is shown in the Falcon CRM portal (menu, name and Settings). */
export function inCrmPortal(path = location.pathname): boolean {
  if (crmOnly()) return true;
  if (!bothPortals()) return false;
  if (CRM_ADDR.test(path)) return true;
  return SHARED_ADDR.test(path) && lastPortal() === "crm";
}
/** Opening a screen moves the person into its portal (shared pages keep the current one). */
export function notePortal(path = location.pathname): void {
  if (!bothPortals() || !path.startsWith("/app")) return;
  if (CRM_ADDR.test(path)) rememberPortal("crm");
  else if (!SHARED_ADDR.test(path)) rememberPortal("plm");
}
